import { computed } from 'vue'
import { FindexClient } from '@/integrations/findex'
import { companyProfileToOption } from '@/form/options/companyProfile'
import type { UseAssetFormState } from '../types'
import { defineField, Validators, type FormFieldDefinition } from '@/form/useForm'
import type { ICompanyProfile, ISavedDocument } from 'findef'
import { useApiHealth } from '@/modules/app/hooks/useApiHealth'

export const companyProfileField = (listed: boolean): FormFieldDefinition<UseAssetFormState, ICompanyProfile | ISavedDocument<ICompanyProfile, string>> => {
  const { isFmpAvailable } = useApiHealth()

  return defineField({
    label: listed ? 'Equity Name' : 'Company Name',
    component: 'select2',
    type: 'dbObject',
    validators: [Validators.required()],
    required: true,
    placeholder: listed ? 'Search tickers...' : 'Search companies...',
    injectedProps: {
      icon: {
        src: async () => import('assets/img/svg/icons/search.svg?raw'),
        stroke: 'currentColor',
        size: '1rem'
      }
    },
    needs: ['isEditMode', 'asset.listed', 'asset.type'],
    options: {
      toOption: (val) => companyProfileToOption(val, listed),
      name: 'ticker-search-dropdown',
      warning: computed(() => {
        if (!listed) return undefined
        return isFmpAvailable.value === false ? 'Tickers are temporarily limited, contact team@myfindex.com for any questions' : undefined
      }),
      add: {
        invokeOnPressEnter: true,
        createItem: async (name) => {
          const [company] = await FindexClient.createCompanyProfile({
            companyName: name,
            listed: listed,
            manuallyAdded: true
          })

          if (!company) {
            return {
              label: 'Error',
              value: null
            }
          }

          return companyProfileToOption(company, listed)
        }
      },
      search: {
        placeholder: listed ? 'Search tickers...' : 'Search companies...',
        emptyStateText: listed ? 'No listed tickers found.' : 'No unlisted companies found.',
        api: {
          fetch: async (query: string) => {
            const [items] = await FindexClient.getCompanyProfiles({
              queryString: query,
              listed: true,
              limit: 100,
              sort: { createdAt: 1 }
            })
            return (items || []).map((it) => companyProfileToOption(it, listed))
          },
          fetchAll: async () => {
            const [items] = await FindexClient.getCompanyProfiles({
              listed: true,
              limit: 100,
              sort: { createdAt: 1 }
            })
            return (items || []).map((it) => companyProfileToOption(it, listed))
          },
          fetchSuggestions: async () => {
            const [items] = await FindexClient.getCompanyProfiles({
              listed: true,
              limit: 100,

              sort: { createdAt: 1 }
            })
            return (items || []).map((it) => companyProfileToOption(it, listed))
          }
        }
      },
      maxVisibleItems: 4
    }
  })
}
